import {Component, Input, OnInit, TemplateRef, ViewChild} from "@angular/core";
import {AgentService, WorkstationAgent} from "../../../../services/back/agent.service";
import {CurrentTenantService} from "../../../../services/front/current-tenant.service";
import {mergeMap, Observable} from "rxjs";
import {DesignSystemModule} from "../../../design-system/design-system.module";
import {ColumnDefinition, TableComponent} from "../../../design-system/table/table.component";
import {tap} from "rxjs/operators";
import {NgClass, NgForOf, NgIf, NgOptimizedImage} from "@angular/common";
import {ChipsModule} from "../../../global/chips/chips/chips.module";
import {QueryRangeType} from "../../../../services/back/tenant-finance.service";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {ButtonWidgetComponent} from "../../../design-system/button-widget/button-widget.component";
import {DropdownTriggerDirective} from "../../../design-system/dropdown/dropdown-trigger.directive";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {RefreshService} from "../refresh.service";
import {SnackbarService} from "../../../../services/front/snackbar.service";
import {SnackbarModule} from "../../../global/snackbar/snackbar.module";
import {DashboardPageLoaderComponent} from "../../../global/title-content-loader/dashboard-page-loader.component";

@Component({
  selector: 'app-agent-list',
  templateUrl: './agent-list.component.html',
  imports: [
    DesignSystemModule,
    TableComponent,
    NgOptimizedImage,
    ChipsModule,
    TranslateModule,
    ButtonWidgetComponent,
    NgForOf,
    DropdownTriggerDirective,
    NgIf,
    ReactiveFormsModule,
    NgClass,
    SnackbarModule,
    DashboardPageLoaderComponent
  ],
  styleUrls: ['./agent-list.component.scss']
})
export class AgentListComponent implements OnInit {
  constructor(
    private agentService: AgentService,
    private tenantService: CurrentTenantService,
    private translateService: TranslateService,
    private snackbarService: SnackbarService,
    private refreshService: RefreshService) { }

  @Input('height') height: string = 'auto';
  @ViewChild('nameTemplate', { static: false }) nameTemplate: TemplateRef<any>;
  @ViewChild('versionTemplate', { static: false }) versionTemplate: TemplateRef<any>;
  @ViewChild('teamTemplate', { static: false }) teamTemplate: TemplateRef<any>;
  @ViewChild('usernameTemplate', { static: false }) usernameTemplate: TemplateRef<any>;
  @ViewChild('statusTemplate', { static: false }) statusTemplate: TemplateRef<any>;
  protected agents: WorkstationAgent[] = [];
  protected filteredAgents: WorkstationAgent[] = [];
  protected filteredAgentPaginated: WorkstationAgent[] = [];
  protected columnDefinitions: ()=>ColumnDefinition<WorkstationAgent>[];
  protected isLoading = true;
  protected availableVersions: string[] = [];
  protected filterVersionForm = new FormControl([]);
  protected searchForm = new FormControl('');
  protected openSearch = false;
  protected pageSize = 100;
  protected pageSizeStep = 50;

  ngOnInit() {
    this.tenantService.getCurrentTenantChanges().pipe(
      mergeMap(tenant => this.agentService.getWorkstationAgents(tenant.configuration.id)),
      tap(() => this.isLoading = false)
    ).subscribe({
      next: response => {
        this.agents = response.agents.sort((a, b) => a.hostname.localeCompare(b.hostname));
        this.filteredAgents = this.agents;
        this.filteredAgentPaginated = this.filteredAgents.slice(0, this.pageSize);
        this.availableVersions = [... new Set(response.agents.map(agent => agent.agentVersion))];
      },
      error: error => this.isLoading = false
    });

    this.columnDefinitions = () => [
      {
        name: 'hostname',
        label: this.translateService.instant('page.agent.workstations.list.table.hostname'),
        template: this.nameTemplate,
        sortFunction: (a, b) => a.hostname.localeCompare(b.hostname),
        width: 31,
      },
      {
        name: 'agentVersion',
        label: this.translateService.instant('page.agent.workstations.list.table.agentVersion'),
        template: this.versionTemplate,
        sortFunction: (a, b) => a.agentVersion.localeCompare(b.agentVersion),
        width: 20,
      },
      {
        name: 'team',
        label: this.translateService.instant('page.agent.workstations.list.table.team'),
        template: this.teamTemplate,
        sortFunction: (a, b) => a.team.localeCompare(b.team),
        width: 31,
      },
      {
        name: 'userRealname',
        label: this.translateService.instant('page.agent.workstations.list.table.userRealname'),
        template: this.usernameTemplate,
        sortFunction: (a, b) => a.userRealname.localeCompare(b.userRealname),
        width: 31,
      },
      {
        name: 'timestamp',
        label: this.translateService.instant('page.agent.workstations.list.table.status'),
        template: this.statusTemplate,
        sortFunction: (a, b) => a.timestamp.localeCompare(b.timestamp),
        width: 20,
      }
    ]

    // Subscribe to refresh
    this.refreshService.onRefresh
      .pipe(mergeMap(() => this.onRefresh()))
      .subscribe();

    // Subscribe to filters
    this.filterVersionForm.valueChanges.pipe(
      tap(() => this.pageSize = this.pageSizeStep),
      tap(() => this.filter()),
    ).subscribe();

    this.searchForm.valueChanges.pipe(
      tap(() => this.pageSize = this.pageSizeStep),
      tap(() => this.filter()),
    ).subscribe();
  }

  increasePageSize() {
    this.pageSize += this.pageSizeStep;
    this.filteredAgentPaginated = this.filteredAgents.slice(0, this.pageSize);
  }

  onRefresh(): Observable<any> {
    return this.tenantService.getCurrentTenantId().pipe(
      mergeMap(tenantId => this.agentService.getWorkstationAgents(tenantId))
    ).pipe(
      tap(response => {
        if (response.agents.length > 0 && this.agents.length > 0 && this.agents.length < response.agents.length) {
          this.snackbarService.show("🎉 " + (response.agents.length - this.agents.length) + this.translateService.instant('page.agent.workstations.list.table.newAgentDiscovered'), "success-snack");
        }
      }),
      tap(response => this.agents = response.agents.sort((a, b) => a.hostname.localeCompare(b.hostname))),
      tap(response => this.filteredAgents = this.agents),
      tap(response => this.filteredAgentPaginated = this.filteredAgents.slice(0, this.pageSize)),
      tap(response => this.availableVersions = [... new Set(response.agents.map(agent => agent.agentVersion))]),
      tap(() => this.filter())
    )
  }

  clearFilters() {
    this.pageSize = this.pageSizeStep;
    this.filterVersionForm.setValue([]);
    this.filter();
  }

  filter() {
    // Filter versions
    if (this.filterVersionForm && this.filterVersionForm.valid && this.filterVersionForm.value && this.filterVersionForm.value.length > 0) {
      const filterToApply: string[] = this.filterVersionForm.value;
      this.filteredAgents = this.agents.filter(agent => filterToApply.includes(agent.agentVersion));
      this.filteredAgentPaginated = this.filteredAgents.slice(0, this.pageSize);
    } else {
      this.filteredAgents = this.agents;
      this.filteredAgentPaginated = this.filteredAgents.slice(0, this.pageSize);
    }

    // Filter search
    if (this.searchForm && this.searchForm.valid && this.searchForm.value && this.searchForm.value.length > 0) {
      const value = (this.searchForm.value as string).toLowerCase();
      this.filteredAgents = this.filteredAgents.filter(agent => {
        return agent.hostname.toLowerCase().includes(value) ||
          agent.userRealname.toLowerCase().includes(value) ||
          agent.teamPath.toLowerCase().includes(value) ||
          agent.agentVersion.toLowerCase().includes(value);
      });
      this.filteredAgentPaginated = this.filteredAgents.slice(0, this.pageSize);
    }
  }

  openSearchPanel(input: HTMLInputElement): void {
    this.openSearch = !this.openSearch;
    if (this.openSearch) {
      input.focus();
    } else {
      this.searchForm.setValue('');
    }
  }

  clearSearch(input: HTMLInputElement): void {
    this.pageSize = this.pageSizeStep;
    this.searchForm.setValue('');
    input.focus();
    this.filter();
  }

  get searchValue(): string {
    return this.searchForm.value as string;
  }

  formatMultiselectLabel(value: any[] | null) : string {
    if (value && value.length > 0) {
      if (value.length > 1) {
        return value[0] + ' (+' + (value.length - 1) + ')';
      } else {
        return value[0];
      }
    } else {
      return '-'
    }
  }

  openDocumentation() {
    window.open(this.translateService.instant('page.agent.workstations.documentationUrl'), '_blank');
  }

  isEllipsisActive(e: HTMLElement): boolean {
    return (e.offsetWidth < e.scrollWidth);
  }

  protected readonly QueryRangeType = QueryRangeType;
}